import { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from './ui/button';
import { Avatar, AvatarFallback } from './ui/avatar';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from './ui/dropdown-menu';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import LabFloLogo from './LabFloLogo';
import TimeClockWidget from './TimeClockWidget';
import GlobalBackground from './GlobalBackground';
import NotificationBell from './NotificationBell';
import OfflineBanner from './OfflineBanner';
import FloatingHelpButton from './FloatingHelpButton';
import { LayoutDashboard, FileText, LogOut, Sun, Moon, Menu, Shield, ShieldCheck, ChevronRight, ChevronDown, PanelLeftClose, PanelLeft, BookOpen, User, Clock, FlaskConical, ClipboardList, LucideIcon, FileHeart, ScrollText, Monitor, GripVertical, ArrowUp, ArrowDown, RotateCcw, Check, Package, Truck, RefreshCw, GitBranch, MessageSquareWarning, Siren, GraduationCap, FileSearch, Beaker } from 'lucide-react';

interface NavItem {
    path: string;
    label: string;
    icon: LucideIcon;
    adminOnly?: boolean;
    hideForShipper?: boolean;
    require503B?: boolean;
    children?: NavItem[];   // if present, this item is expandable
    parentPath?: string;    // set on child items — the parent's path for grouping
    group?: string;         // section header name (503B only)
}

// 503B sidebar section groupings — order here defines section order in the UI.
// Each section is collapsible with per-user persistence + a coloured indicator
// bar so techs can differentiate Operations from Quality at a glance.
const NAV_GROUPS_503B: { key: string; label: string; tint: string }[] = [
    { key: 'home',           label: 'Home',             tint: 'bg-sky-400' },
    { key: 'operations',     label: 'Operations',       tint: 'bg-violet-400' },
    { key: 'quality',        label: 'Quality',          tint: 'bg-rose-400' },
    { key: 'people_reports', label: 'People & Reports', tint: 'bg-emerald-400' },
    { key: 'personal',       label: 'Personal',         tint: 'bg-amber-400' },
];

// Standard 503A / clinic-facing pharmacy nav
const navItems: NavItem[] = [
    { path: '/emr', label: 'EMR Orders', icon: FileHeart },
    { path: '/timecard', label: 'Timecard', icon: Clock, hideForShipper: true },
    { path: '/dashboard', label: 'Tasks Dashboard', icon: LayoutDashboard, hideForShipper: true },
    { path: '/profile', label: 'Profile', icon: User },
    { path: '/compounding', label: 'Compounding', icon: ClipboardList, hideForShipper: true },
    { path: '/inventory', label: 'API Inventory & Supplies', icon: FlaskConical, hideForShipper: true },
    { path: '/wallboard', label: 'Wallboard', icon: Monitor, hideForShipper: true },
    { path: '/reports', label: 'Reports & Audit', icon: FileText, hideForShipper: true },
    { path: '/sops', label: 'Compliance/SOPs', icon: ScrollText, hideForShipper: true },
    { path: '/training', label: 'Training', icon: BookOpen, hideForShipper: true },
    { path: '/admin', label: 'Admin', icon: Shield, adminOnly: true },
];

// 503B outsourcing-facility nav.
// All modules from the 6-phase 503B compliance suite are exposed as first-class
// sidebar items. The user can drag-to-reorder them via the Reorder toggle in
// the sidebar footer (persisted per user + tenant in localStorage).
const navItems503B: NavItem[] = [
    // ── Home ─────────────────────────────────────────────
    { path: '/503b', label: '503B Dashboard', icon: LayoutDashboard, group: 'home' },
    { path: '/wallboard', label: 'Wallboard', icon: Monitor, group: 'home' },

    // ── Operations ───────────────────────────────────────
    { path: '/orders', label: 'Customer Orders', icon: FileText, group: 'operations' },
    { path: '/503b/ebr', label: 'Batch Records (EBR)', icon: ClipboardList, group: 'operations' },
    { path: '/503b/lims', label: 'LIMS', icon: Beaker, group: 'operations' },
    { path: '/503b/inventory-ops', label: 'Inventory & Warehouse', icon: Package, group: 'operations' },
    { path: '/503b/procurement', label: 'Procurement & Vendors', icon: Truck, group: 'operations' },
    { path: '/formulas', label: 'Master Formulas', icon: FlaskConical, group: 'operations' },

    // ── Quality Management ───────────────────────────────
    { path: '/503b/capa', label: 'Deviations & CAPA', icon: RefreshCw, group: 'quality' },
    { path: '/503b/change-control', label: 'Change Control', icon: GitBranch, group: 'quality' },
    { path: '/503b/complaints', label: 'Complaints & NCPs', icon: MessageSquareWarning, group: 'quality' },
    { path: '/503b/recalls', label: 'Product Recalls', icon: Siren, group: 'quality' },
    { path: '/503b/stability', label: 'Stability & APR', icon: FlaskConical, group: 'quality' },

    // ── People & Reports ─────────────────────────────────
    { path: '/503b/training', label: 'Training / LMS', icon: GraduationCap, group: 'people_reports' },
    { path: '/503b/reports', label: 'Reports & Inspection', icon: FileSearch, group: 'people_reports' },
    { path: '/compliance-center', label: 'Audit Trail & Sign-off', icon: ShieldCheck, group: 'people_reports' },
    { path: '/sops', label: 'SOPs Library', icon: ScrollText, group: 'people_reports' },

    // ── Personal & Admin ─────────────────────────────────
    { path: '/timecard', label: 'Timecard', icon: Clock, hideForShipper: true, group: 'personal' },
    { path: '/profile', label: 'Profile', icon: User, group: 'personal' },
    { path: '/admin', label: 'Admin', icon: Shield, adminOnly: true, group: 'personal' },
];

interface LayoutProps {
    children: React.ReactNode;
}

export default function Layout({ children }: LayoutProps) {
    const { user, logout, isAdmin, token, userRole } = useAuth();
    const { tenant, tenantSlug } = useTenant();
    const { theme, toggleTheme } = useTheme();
    const navigate = useNavigate();
    const location = useLocation();
    const [mobileOpen, setMobileOpen] = useState(false);
    const [timeclockEnabled, setTimeclockEnabled] = useState(true);
    const [is503B, setIs503B] = useState<boolean>(false);
    const [collapsed, setCollapsed] = useState<boolean>(() => {
        try { return localStorage.getItem('labflo_sidebar_collapsed') === '1'; } catch { return false; }
    });
    const [editOrder, setEditOrder] = useState<boolean>(false);
    const [customOrder, setCustomOrder] = useState<string[]>([]);
    const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>(() => {
        try { return JSON.parse(localStorage.getItem('labflo_sidebar_expanded') || '{}'); } catch { return {}; }
    });
    // Collapsed-state for 503B section headers (Home / Operations / Quality / …)
    const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>(() => {
        try { return JSON.parse(localStorage.getItem('labflo_sidebar_sections_collapsed') || '{}'); } catch { return {}; }
    });
    const toggleSection = (key: string) => {
        setCollapsedSections(prev => {
            const next = { ...prev, [key]: !prev[key] };
            try { localStorage.setItem('labflo_sidebar_sections_collapsed', JSON.stringify(next)); } catch { /* ignore */ }
            return next;
        });
    };

    const toggleCollapsed = () => {
        setCollapsed(prev => {
            const next = !prev;
            try { localStorage.setItem('labflo_sidebar_collapsed', next ? '1' : '0'); } catch { /* ignore */ }
            return next;
        });
    };

    // Ctrl/Cmd+B keyboard shortcut to collapse/expand the sidebar
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if ((e.ctrlKey || e.metaKey) && (e.key === 'b' || e.key === 'B')) {
                // Don't override browser's bookmark bar shortcut when an input has focus
                const tag = (document.activeElement?.tagName || '').toUpperCase();
                if (tag === 'INPUT' || tag === 'TEXTAREA' || (document.activeElement as HTMLElement | null)?.isContentEditable) return;
                e.preventDefault();
                toggleCollapsed();
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, []);

    useEffect(() => {
        const fetchTimeclockStatus = async () => {
            try {
                const backendUrl = process.env.REACT_APP_BACKEND_URL;
                if (!backendUrl) return;
                
                const res = await fetch(`${backendUrl}/api/settings/timeclock-status`);
                if (res.ok) {
                    const data = await res.json();
                    setTimeclockEnabled(data.timeclock_enabled !== false);
                }
            } catch { /* silent */ }
        };
        fetchTimeclockStatus();

        // Re-fetch when payroll settings change (dispatched from AdminPage)
        const handler = () => fetchTimeclockStatus();
        window.addEventListener('payroll-settings-updated', handler);
        return () => window.removeEventListener('payroll-settings-updated', handler);
    }, []);

    // Probe 503B eligibility so we can conditionally show the 503B sidebar item.
    // Auth uses HTTPOnly cookies, so we must send credentials — not a Bearer
    // token. Requiring `token` here was a bug that left the 503B sidebar
    // permanently on the 503A layout for cookie-authed sessions.
    useEffect(() => {
        let cancelled = false;
        const check = async () => {
            try {
                const backendUrl = process.env.REACT_APP_BACKEND_URL;
                if (!backendUrl || !tenantSlug) { setIs503B(false); return; }
                const res = await fetch(`${backendUrl}/api/503b/tenant-status`, {
                    credentials: 'include',
                    headers: {
                        ...(token ? { Authorization: `Bearer ${token}` } : {}),
                        'X-Tenant-Slug': tenantSlug,
                    },
                });
                if (!cancelled) setIs503B(res.ok);
            } catch {
                if (!cancelled) setIs503B(false);
            }
        };
        check();
        return () => { cancelled = true; };
    }, [token, tenantSlug]);

    const handleLogout = () => {
        const slug = sessionStorage.getItem('tenant_slug') || '';
        const targetUrl = slug ? `/login?tenant=${slug}` : '/login';
        navigate(targetUrl);
        logout?.();
    };

    const isShipper = userRole === 'shipper';

    // 503B tenants get a completely different nav list + emerald accent
    const activeNavList = is503B ? navItems503B : navItems;

    // Per-user order key — scoped by tenant + username + mode so 503B and 503A
    // don't step on each other and switching tenants doesn't leak preferences.
    const orderStorageKey = `labflo_nav_order__${tenantSlug || 'default'}__${user?.username || 'anon'}__${is503B ? '503b' : '503a'}`;

    // Load persisted order (and reset when the mode / tenant / user changes)
    useEffect(() => {
        try {
            const saved = localStorage.getItem(orderStorageKey);
            setCustomOrder(saved ? JSON.parse(saved) : []);
        } catch {
            setCustomOrder([]);
        }
    }, [orderStorageKey]);

    // Persist expanded groups
    useEffect(() => {
        try { localStorage.setItem('labflo_sidebar_expanded', JSON.stringify(expandedGroups)); } catch { /* ignore */ }
    }, [expandedGroups]);

    const persistOrder = (order: string[]) => {
        setCustomOrder(order);
        try { localStorage.setItem(orderStorageKey, JSON.stringify(order)); } catch { /* ignore */ }
    };

    const restoreDefaults = () => {
        try { localStorage.removeItem(orderStorageKey); } catch { /* ignore */ }
        setCustomOrder([]);
        setEditOrder(false);
    };

    const filteredNavItems = activeNavList.filter(item => {
        if (item.adminOnly && !isAdmin) return false;
        if (item.path === '/timecard' && !timeclockEnabled) return false;
        if (item.hideForShipper && isShipper) return false;
        if (item.require503B && !is503B) return false;
        return true;
    });

    // Apply persisted custom order — unknown / new items stay at the end in
    // their original position so new nav items appear automatically.
    const orderedNavItems: NavItem[] = (() => {
        if (!customOrder.length) return filteredNavItems;
        const byPath = new Map(filteredNavItems.map(i => [i.path, i]));
        const seen = new Set<string>();
        const result: NavItem[] = [];
        for (const p of customOrder) {
            const item = byPath.get(p);
            if (item) { result.push(item); seen.add(p); }
        }
        for (const item of filteredNavItems) {
            if (!seen.has(item.path)) result.push(item);
        }
        return result;
    })();

    const moveItem = (idx: number, dir: -1 | 1) => {
        const newIdx = idx + dir;
        if (newIdx < 0 || newIdx >= orderedNavItems.length) return;
        const paths = orderedNavItems.map(i => i.path);
        [paths[idx], paths[newIdx]] = [paths[newIdx], paths[idx]];
        persistOrder(paths);
    };

    // Accent tokens — swap to emerald for 503B outsourcing tenants
    const accentActive = is503B ? 'bg-emerald-600 text-white' : 'bg-primary text-primary-foreground';
    const accentHover = is503B ? 'hover:bg-emerald-500/10' : 'hover:bg-violet-500/10';
    const accentBorder = is503B ? 'dark:border-emerald-500/15' : 'dark:border-violet-500/10';
    const shellSubtitle = is503B ? '503B Manufacturing' : 'Pharmacy & Cleanroom Tracking';

    const isActivePath = (path: string, children?: NavItem[]) => {
        if (location.pathname === path) return true;
        if (children && children.some(c => location.pathname === c.path)) return true;
        return false;
    };

    return (
        <div className="main-layout" data-testid="main-layout">
            <GlobalBackground />
            <OfflineBanner />
            {/* Desktop Sidebar */}
            <aside
                className={`sidebar hidden md:flex print:hidden flex-col h-screen dark:bg-[#080016]/90 dark:border-r ${accentBorder} transition-[width] duration-200 ease-out ${collapsed ? 'sidebar-collapsed' : ''}`}
                style={{ width: collapsed ? '4.25rem' : '16rem', position: 'sticky', top: 0, alignSelf: 'flex-start' }}
                data-testid="sidebar"
                data-collapsed={collapsed ? 'true' : 'false'}
                data-mode={is503B ? '503b' : '503a'}
            >
                {/* Logo + Collapse toggle */}
                <div className={`p-4 border-b border-border ${accentBorder} flex items-center justify-between gap-2`}>
                    <div className={`flex items-center gap-3 overflow-hidden ${collapsed ? 'justify-center w-full' : ''}`}>
                        <LabFloLogo size={collapsed ? 32 : 40} />
                        {!collapsed && (
                            <div className="min-w-0">
                                <h1 className="text-lg font-bold truncate flex items-center gap-1.5" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                    LabFlo<sup className="text-[9px] align-super">℠</sup>
                                    {is503B && (
                                        <ShieldCheck className="w-3.5 h-3.5 text-emerald-500" data-testid="sidebar-503b-badge" />
                                    )}
                                </h1>
                                <p className={`text-xs truncate ${is503B ? 'text-emerald-400' : 'text-muted-foreground'}`}>{shellSubtitle}</p>
                            </div>
                        )}
                    </div>
                    {!collapsed && (
                        <button
                            type="button"
                            onClick={toggleCollapsed}
                            className={`shrink-0 p-1.5 rounded-md text-muted-foreground hover:text-foreground ${accentHover} transition`}
                            title="Collapse sidebar (Ctrl+B)"
                            aria-label="Collapse sidebar"
                            data-testid="sidebar-collapse-btn"
                        >
                            <PanelLeftClose className="w-4 h-4" />
                        </button>
                    )}
                </div>

                {collapsed && (
                    <div className="px-2 pt-2">
                        <button
                            type="button"
                            onClick={toggleCollapsed}
                            className={`w-full flex items-center justify-center p-2 rounded-md text-muted-foreground hover:text-foreground ${accentHover} transition`}
                            title="Expand sidebar (Ctrl+B)"
                            aria-label="Expand sidebar"
                            data-testid="sidebar-expand-btn"
                        >
                            <PanelLeft className="w-4 h-4" />
                        </button>
                    </div>
                )}

                {/* Navigation */}
                <div className="flex-1 py-4 overflow-y-auto">
                    <nav className="space-y-1 px-2" data-testid="sidebar-nav">
                        {orderedNavItems.map((item, idx) => {
                            const hasChildren = !!item.children?.length;
                            const active = isActivePath(item.path, item.children);
                            const groupOpen = hasChildren && (expandedGroups[item.path] ?? active);

                            // ── Section header injection (503B only) ────────────
                            // When the `group` key changes between adjacent items,
                            // emit a collapsible section header before this item.
                            let sectionHeader: React.ReactNode = null;
                            if (is503B && item.group) {
                                const prevGroup = idx > 0 ? orderedNavItems[idx - 1].group : undefined;
                                if (item.group !== prevGroup) {
                                    const groupMeta = NAV_GROUPS_503B.find(g => g.key === item.group);
                                    const groupLabel = groupMeta?.label || item.group;
                                    const isSectionCollapsed = !!collapsedSections[item.group];
                                    sectionHeader = !collapsed && (
                                        <button
                                            type="button"
                                            onClick={() => toggleSection(item.group!)}
                                            className="w-full flex items-center gap-2 px-2 pt-3 pb-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground/70 hover:text-foreground transition-colors"
                                            data-testid={`sidebar-section-${item.group}`}
                                            aria-expanded={!isSectionCollapsed}
                                        >
                                            {isSectionCollapsed ? <ChevronRight className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                                            <span
                                                className={`w-1.5 h-1.5 rounded-full ${groupMeta?.tint || 'bg-muted-foreground/30'} shadow-[0_0_6px_currentColor]`}
                                                aria-hidden="true"
                                            />
                                            <span>{groupLabel}</span>
                                            <div className={`flex-1 h-px ml-1 ${groupMeta?.tint || 'bg-border/40'} opacity-30`} />
                                        </button>
                                    );
                                }
                                // Hide this item if its section is collapsed
                                if (collapsedSections[item.group] && !collapsed) {
                                    return sectionHeader ? <div key={`section-${item.group}`}>{sectionHeader}</div> : null;
                                }
                            }

                            return (
                                <div key={item.path} data-testid={`nav-group-${item.path.replace(/\//g, '')}`}>
                                    {sectionHeader}
                                    <div className="flex items-center gap-1">
                                        {editOrder && !collapsed && (
                                            <div className="flex flex-col shrink-0" data-testid={`nav-reorder-${item.path.replace(/\//g, '')}`}>
                                                <button
                                                    type="button"
                                                    onClick={() => moveItem(idx, -1)}
                                                    disabled={idx === 0}
                                                    className="p-0.5 rounded hover:bg-muted disabled:opacity-30"
                                                    aria-label="Move up"
                                                    data-testid={`nav-move-up-${item.path.replace(/\//g, '')}`}
                                                >
                                                    <ArrowUp className="w-3 h-3" />
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => moveItem(idx, 1)}
                                                    disabled={idx === orderedNavItems.length - 1}
                                                    className="p-0.5 rounded hover:bg-muted disabled:opacity-30"
                                                    aria-label="Move down"
                                                    data-testid={`nav-move-down-${item.path.replace(/\//g, '')}`}
                                                >
                                                    <ArrowDown className="w-3 h-3" />
                                                </button>
                                            </div>
                                        )}
                                        {hasChildren ? (
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    if (collapsed) {
                                                        navigate(item.path);
                                                    } else {
                                                        setExpandedGroups(prev => ({ ...prev, [item.path]: !groupOpen }));
                                                    }
                                                }}
                                                title={collapsed ? item.label : undefined}
                                                className={`flex-1 flex items-center ${collapsed ? 'justify-center' : 'gap-3'} px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                                                    active
                                                        ? accentActive
                                                        : `${accentHover} text-muted-foreground hover:text-foreground`
                                                }`}
                                                data-testid={`nav-${item.path.replace('/', '')}`}
                                                aria-label={item.label}
                                            >
                                                {editOrder && !collapsed && <GripVertical className="w-3.5 h-3.5 opacity-40 shrink-0" />}
                                                <item.icon className="w-4 h-4 shrink-0" />
                                                {!collapsed && (
                                                    <>
                                                        <span className="truncate flex-1 text-left">{item.label}</span>
                                                        {groupOpen ? <ChevronDown className="w-3.5 h-3.5 opacity-70" /> : <ChevronRight className="w-3.5 h-3.5 opacity-70" />}
                                                    </>
                                                )}
                                            </button>
                                        ) : (
                                            <Link
                                                to={item.path}
                                                title={collapsed ? item.label : undefined}
                                                className={`flex-1 flex items-center ${collapsed ? 'justify-center' : 'gap-3'} px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                                                    active
                                                        ? accentActive
                                                        : `${accentHover} text-muted-foreground hover:text-foreground`
                                                }`}
                                                data-testid={`nav-${item.path.replace('/', '')}`}
                                                aria-label={item.label}
                                            >
                                                {editOrder && !collapsed && <GripVertical className="w-3.5 h-3.5 opacity-40 shrink-0" />}
                                                <item.icon className="w-4 h-4 shrink-0" />
                                                {!collapsed && <span className="truncate">{item.label}</span>}
                                            </Link>
                                        )}
                                    </div>
                                    {hasChildren && groupOpen && !collapsed && (
                                        <div className="ml-4 pl-3 border-l border-border/60 mt-1 space-y-1">
                                            {item.children!.map(child => {
                                                const childActive = location.pathname === child.path;
                                                return (
                                                    <Link
                                                        key={child.path}
                                                        to={child.path}
                                                        className={`flex items-center gap-3 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                                                            childActive
                                                                ? accentActive
                                                                : `${accentHover} text-muted-foreground hover:text-foreground`
                                                        }`}
                                                        data-testid={`nav-child-${child.path.replace(/\//g, '')}`}
                                                    >
                                                        <child.icon className="w-3.5 h-3.5 shrink-0" />
                                                        <span className="truncate">{child.label}</span>
                                                    </Link>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </nav>
                    {editOrder && !collapsed && (
                        <div className="mt-3 mx-3 rounded-md border border-emerald-500/30 bg-emerald-500/5 p-2 text-[11px] text-emerald-300 leading-snug">
                            Sidebar edit mode — use ↑ ↓ to reorder. Changes save automatically.
                        </div>
                    )}
                </div>

                {/* Time Clock Widget — hidden when collapsed (too wide); user can expand to use it */}
                {timeclockEnabled && !collapsed && (
                    <div className="px-3 pb-3 print:hidden" data-testid="sidebar-time-clock">
                        <TimeClockWidget />
                    </div>
                )}

                {/* User Section */}
                <div className={`p-3 border-t border-border dark:border-violet-500/10 ${collapsed ? 'flex flex-col items-center gap-2' : ''}`}>
                    {!collapsed && (
                        <div className="flex items-center justify-between mb-2">
                            <NotificationBell token={token || ''} />
                        </div>
                    )}
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button
                                variant="ghost"
                                className={collapsed ? 'w-10 h-10 p-0 justify-center' : 'w-full justify-start gap-3 h-auto py-2'}
                                title={collapsed ? `${user?.username} · open menu` : undefined}
                                data-testid="sidebar-user-menu-btn"
                            >
                                <Avatar className="w-8 h-8">
                                    <AvatarFallback className="bg-primary/10 text-primary text-sm">
                                        {user?.username?.charAt(0)?.toUpperCase() || 'U'}
                                    </AvatarFallback>
                                </Avatar>
                                {!collapsed && (
                                    <>
                                        <div className="flex-1 text-left min-w-0">
                                            <p className="text-sm font-medium truncate">{user?.username}</p>
                                            <p className="text-xs text-muted-foreground truncate">{user?.is_admin ? 'Master Admin' : 'User'}</p>
                                        </div>
                                        <ChevronRight className="w-4 h-4 text-muted-foreground" />
                                    </>
                                )}
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-56">
                            <div className="px-2 py-1.5">
                                <p className="text-sm font-medium">{user?.username}</p>
                                <p className="text-xs text-muted-foreground">{user?.is_admin ? 'Master Admin' : 'User'}</p>
                            </div>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={toggleCollapsed} data-testid="sidebar-collapse-menuitem">
                                {collapsed ? (
                                    <>
                                        <PanelLeft className="w-4 h-4 mr-2" />
                                        Expand sidebar
                                    </>
                                ) : (
                                    <>
                                        <PanelLeftClose className="w-4 h-4 mr-2" />
                                        Collapse sidebar
                                    </>
                                )}
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={toggleTheme} data-testid="theme-toggle">
                                {theme === 'dark' ? (
                                    <>
                                        <Sun className="w-4 h-4 mr-2" />
                                        Light Mode
                                    </>
                                ) : (
                                    <>
                                        <Moon className="w-4 h-4 mr-2" />
                                        Dark Mode
                                    </>
                                )}
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => setEditOrder(v => !v)} data-testid="sidebar-edit-order-menuitem">
                                {editOrder ? (
                                    <>
                                        <Check className="w-4 h-4 mr-2 text-emerald-500" />
                                        Done editing order
                                    </>
                                ) : (
                                    <>
                                        <GripVertical className="w-4 h-4 mr-2" />
                                        Edit sidebar order
                                    </>
                                )}
                            </DropdownMenuItem>
                            <DropdownMenuItem
                                onClick={restoreDefaults}
                                disabled={!customOrder.length && !editOrder}
                                data-testid="sidebar-restore-defaults-menuitem"
                            >
                                <RotateCcw className="w-4 h-4 mr-2" />
                                Restore default order
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={handleLogout} className="text-destructive focus:text-destructive" data-testid="logout-btn">
                                <LogOut className="w-4 h-4 mr-2" />
                                Logout
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
            </aside>

            {/* Mobile Header */}
            <div className="md:hidden glass-header fixed top-0 left-0 right-0 z-50">
                <div className="flex items-center justify-between p-4">
                    <div className="flex items-center gap-3">
                        <LabFloLogo size={32} />
                        <span className="font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>LabFlo<sup className="text-[7px] align-super">℠</sup></span>
                    </div>
                    <div className="flex items-center gap-2">
                        <NotificationBell token={token || ''} />
                        <Button variant="ghost" size="icon" onClick={toggleTheme} data-testid="mobile-theme-toggle">
                            {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
                        </Button>
                        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                            <SheetTrigger asChild>
                                <Button variant="ghost" size="icon" data-testid="mobile-menu-btn">
                                    <Menu className="w-5 h-5" />
                                </Button>
                            </SheetTrigger>
                            <SheetContent side="right" className="w-64 p-0">
                                <div className="flex flex-col h-full">
                                    <div className="p-6 border-b border-border">
                                        <div className="flex items-center gap-3">
                                            <Avatar className="w-10 h-10">
                                                <AvatarFallback className="bg-primary/10 text-primary">
                                                    {user?.username?.charAt(0)?.toUpperCase() || 'U'}
                                                </AvatarFallback>
                                            </Avatar>
                                            <div>
                                                <p className="font-medium">{user?.username}</p>
                                                <p className="text-xs text-muted-foreground">{user?.is_admin ? 'Master Admin' : 'User'}</p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex-1 py-6">
                                        <nav className="space-y-1 px-3">
                                            {orderedNavItems.map((item) => (
                                                <Link
                                                    key={item.path}
                                                    to={item.path}
                                                    onClick={() => setMobileOpen(false)}
                                                    className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                                                        location.pathname === item.path
                                                            ? accentActive
                                                            : 'hover:bg-muted text-muted-foreground hover:text-foreground'
                                                    }`}
                                                    data-testid={`mobile-nav-${item.path.replace('/', '')}`}
                                                >
                                                    <item.icon className="w-4 h-4" />
                                                    {item.label}
                                                </Link>
                                            ))}
                                        </nav>
                                    </div>
                                    <div className="p-4 border-t border-border">
                                        <Button 
                                            variant="destructive" 
                                            className="w-full" 
                                            onClick={handleLogout}
                                            data-testid="mobile-logout-btn"
                                        >
                                            <LogOut className="w-4 h-4 mr-2" />
                                            Logout
                                        </Button>
                                    </div>
                                </div>
                            </SheetContent>
                        </Sheet>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <main className="content-area md:pt-0 pt-16 bg-background relative">
                <div className="pointer-events-none absolute inset-0 dark:opacity-10 opacity-0 bg-[linear-gradient(90deg,rgba(139,92,246,0.06)_1px,transparent_1px),linear-gradient(180deg,rgba(139,92,246,0.06)_1px,transparent_1px)] bg-[size:60px_60px] print:hidden" />
                <div className="relative z-[1]">
                    {children}
                </div>
                <footer className="text-center py-4 text-xs text-muted-foreground/50 print:hidden">
                    © {new Date().getFullYear()} LabFlo LLC. All rights reserved.
                    <span className="mx-2">·</span>
                    <a href="/terms" className="hover:text-foreground/70 transition-colors">Terms</a>
                    <span className="mx-2">·</span>
                    <a href="/privacy" className="hover:text-foreground/70 transition-colors">Privacy</a>
                    <span className="mx-2">·</span>
                    {/* Supply-store link temporarily hidden
                    <a
                        href="https://www.suncoastvials.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:text-cyan-200 text-cyan-300 transition-colors font-medium"
                        data-testid="footer-wholesale-link"
                    >
                        Order Supplies at SunCoastVials
                    </a>
                    */}
                </footer>
            </main>
            <div className="print:hidden">
                <FloatingHelpButton
                    tenantSlug={tenantSlug || 'default'}
                    tenantName={tenant?.name || 'Pharmacy'}
                    tenantType="pharmacy"
                    userName={(user as any)?.full_name || user?.username}
                    userEmail=""
                />
            </div>
        </div>
    );
}
